/* eslint-disable enforce-catch-error-handled/require-catch-error-handling */
// TODO: Remove this eslint-disable comment in a separate PR (RCP-27576)
import {
  CreateIssueRequestDto,
  CreateIssueRequestDtoSeverityEnum,
  CreateIssueResponseDto,
  IssueSourceDto,
} from '@issuemanager/api-client';
import {
  CollapsibleField,
  ControlledDateField,
  ControlledFileUploader,
  ControlledTextField,
  ExtendedControlledAutocomplete,
  ExtendedTypography,
  GovmapGroupsSelector,
  ReadOnlyText,
  UserSelector,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useBadRequestOrConflictError,
  useIssueTypeIssueSubtypeInfiniteScroll,
  useMonitorCreateIssueFormIssueSubtypesLoad,
  useMonitorCreateIssueFormLoad,
  useMonitorCreateIssueFormSubmit,
} from '@issuemanager/apps/client/hooks';
import {
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import {
  useCreateIssue,
  useLocalizedIssueTypes,
  useSeverityLocalizer,
} from '@issuemanager/client/features/issues';
import {
  ISSUE_DESCRIPTION_LENGTH_MAX,
  ISSUE_TITLE_LENGTH_MAX,
  IssueType,
} from '@issuemanager/constants';
import {
  AutocompleteInputChangeReason,
  Link,
  Stack,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

type LocalFields =
  | 'title'
  | 'issueType'
  | 'issueSubtype'
  | 'description'
  | 'attachments'
  | 'information'
  | 'source'
  | 'govmapGroups'
  | 'dueDate'
  | 'ownerId'
  | 'severity';

export type UseCreateIssueFormProps = {
  information?: string;
  onCreated: (values: CreateIssueResponseDto) => void;
  onError?: (error: unknown) => void;
  answerIds?: string[];
  defaultValues?: Partial<
    Omit<CreateIssueRequestDto, 'answerIds' | 'attachments'>
  >;
  sourceInfo?: IssueSourceDto;
  issueType?: IssueType;
  open?: boolean;
  isSourceError?: boolean;
  isSourceExpected?: boolean;
};

export const useCreateIssueForm = (props: UseCreateIssueFormProps) => {
  const {
    open = true,
    onCreated,
    answerIds,
    information,
    isSourceError,
    isSourceExpected,
    onError,
    sourceInfo,
    issueType,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: isAttachmentLoading } = useAttachmentsContext();

  const { t } = useTranslation(['issue', 'common']);
  const { errors: serviceErrors, cleanErrors: cleanServiceErrors } =
    useBadRequestOrConflictError();

  const createIssue = useCreateIssue({
    onSuccess: (values) => {
      enqueueSnackbar('', {
        variant: 'success',
        description: (
          <Trans
            t={t}
            i18nKey='form.tooltip.success'
            components={{ b: <b /> }}
            values={{ issueCode: values.code }}
          />
        ),
      });
    },
  });

  const defaultValues = useMemo(
    () => ({
      description: '',
      attachments: [],
      issueTypeId: '',
      issueSubtypeId: '',
      ...props.defaultValues,
    }),
    [props.defaultValues],
  );

  const form = useForm<CreateIssueRequestDto>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const selectedIssueType = form.watch('issueTypeId');

  const {
    issueTypeOptions,
    subtypesOptions,
    handleInputChange,
    isLoading: issueTypeSubtypeQueryIsLoading,
    issueTypesQuery,
    issueSubtypesQuery,
  } = useIssueTypeIssueSubtypeInfiniteScroll({
    selectedIssueType,
    issueTypeQueryParams: {
      filterByName: issueType || undefined,
    },
    issueSubtypeQueryParams: {
      handleError: true,
    },
  });

  const localizedIssueTypes = useLocalizedIssueTypes();
  const getSeverityLabel = useSeverityLocalizer();
  const severityValues = Object.entries(CreateIssueRequestDtoSeverityEnum).map(
    ([_key, value]) => ({
      label: (getSeverityLabel(value) ?? value) as string,
      value,
    }),
  );

  // TODO: Replicate this in all form hooks that can potentially be used in a side panel
  // When the form is closed, reset the form to the default values
  useEffect(() => {
    if (open === false) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, open]);

  useEffect(() => {
    if (!issueType || !issueTypeOptions?.length) return;

    const matchedIssueType = issueTypeOptions.find(
      (option) => option.name === issueType,
    );

    if (matchedIssueType?.id) {
      form.setValue('issueTypeId', matchedIssueType.id);
    } else {
      // Ensure we have a valid value even if no match is found
      form.setValue('issueTypeId', '');
    }
  }, [issueType, issueTypesQuery.dataUpdatedAt, form]);

  const monitorCreateIssueFormLoad = useMonitorCreateIssueFormLoad();
  const monitorCreateIssueFormIssueSubtypesLoad =
    useMonitorCreateIssueFormIssueSubtypesLoad();
  const monitorCreateIssueFormSubmit = useMonitorCreateIssueFormSubmit();

  useAllQueries({
    queries: [issueTypesQuery],
    onSuccess: () => {
      monitorCreateIssueFormLoad.stop();
    },
    onError: () => {
      monitorCreateIssueFormLoad.error();
    },
  });

  useAllQueries({
    queries: [issueSubtypesQuery],
    onStart: () => {
      monitorCreateIssueFormIssueSubtypesLoad.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormIssueSubtypesLoad.stop();
    },
    onError: () => {
      monitorCreateIssueFormIssueSubtypesLoad.error();
    },
  });

  useAllMutations({
    mutations: [createIssue],
    onStart: () => {
      monitorCreateIssueFormSubmit.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormSubmit.stop();
    },
    onError: () => {
      monitorCreateIssueFormSubmit.error();
    },
  });

  const cleanErrors = (): void => {
    form.clearErrors();
    cleanServiceErrors();
  };

  const onSubmit = useEventCallback(() => {
    if (isAttachmentLoading) {
      enqueueSnackbar(t('issue:form.attachments.uploadInProgress.message'), {
        variant: 'info',
      });
      return;
    }

    form.handleSubmit(async (values) => {
      let { description } = values;
      if (information) {
        description = `${information} :: ${description}`;
      }

      try {
        const issueResponse = await createIssue.mutateAsync({
          ...values,
          description,
          answerIds,
          source: sourceInfo,
        });
        onCreated(issueResponse);
      } catch (error) {
        onError?.(error);
        // eslint-disable-next-line no-console
        console.error(error);
      }
    })();
  });

  const rules = {
    title: {
      required: t('issue:form.errorMessages.title.required'),
    },
    issueType: {
      required: t('issue:form.errorMessages.issueType.required'),
    },
    description: {
      required: t('issue:form.errorMessages.description.required'),
      validate: (_: any, values: CreateIssueRequestDto) => {
        const trimmedValue = (values.description || '').trim();
        if (trimmedValue.length === 0) {
          return t('issue:form.errorMessages.description.required');
        }
        if (trimmedValue.length > ISSUE_DESCRIPTION_LENGTH_MAX) {
          return t('issue:form.errorMessages.description.maxLength');
        }
        return true;
      },
    },
  };

  const labels = {
    issueTypeId: t('issue:form.labels.issueType'),
    subtypeId: t('issue:form.labels.issueSubtype'),
    title: t('issue:form.labels.title'),
    relatedOrgUnit: t('issue:form.labels.relatedOrgUnit'),
    description: t('issue:form.labels.description'),
    information: t('issue:form.labels.information'),
    source: t('issue:form.labels.source'),
    attachments: t('issue:form.labels.attachments'),
    dueDate: t('issue:form.labels.dueDate'),
    ownerId: t('issue:form.labels.owner'),
    severity: t('issue:form.labels.severity'),
  };

  const fields: Record<LocalFields, ReactNode> = {
    title: (
      <ControlledTextField
        name='title'
        label={labels.title}
        control={form.control}
        rules={{
          required: t('issue:form.errorMessages.title.required'),
        }}
        maxLength={ISSUE_TITLE_LENGTH_MAX}
      />
    ),
    issueType: (
      <ControlledTextField
        name='issueTypeId'
        data-testid='categoryId'
        label={labels.issueTypeId}
        control={form.control}
        rules={rules.issueType}
        options={issueTypeOptions.map((issueType) => ({
          value: issueType.id,
          label:
            localizedIssueTypes[issueType.name as IssueType] ?? issueType.name,
        }))}
        onChange={() => {
          form.setValue('issueSubtypeId', undefined);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    issueSubtype: (
      <ExtendedControlledAutocomplete
        name='issueSubtypeId'
        label={labels.subtypeId}
        data-testid='subcategoryId'
        control={form.control}
        loading={issueSubtypesQuery.isFetching}
        options={subtypesOptions.map((issueSubtype) => ({
          id: issueSubtype.id,
          label: issueSubtype.name,
        }))}
        onTextChange={(
          value: string,
          reason: AutocompleteInputChangeReason,
        ) => {
          handleInputChange(value, 'issueSubtype', reason);
        }}
        placeholder={t('common:inputs.listBox.empty')}
      />
    ),
    govmapGroups: (
      <Controller
        name='govmapGroups'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <GovmapGroupsSelector
            value={value ?? []}
            onChange={onChange}
            label={labels.relatedOrgUnit}
            error={!!error?.message}
            helperText={error?.message}
          />
        )}
      />
    ),
    description: (
      <ControlledTextField
        name='description'
        label={labels.description}
        control={form.control}
        rules={rules.description}
        minRows={4}
        multiline
        maxLength={ISSUE_DESCRIPTION_LENGTH_MAX}
      />
    ),
    attachments: (
      <ControlledFileUploader
        disabled={createIssue.isPending}
        control={form.control}
        label={labels.attachments}
        name='attachments'
        permissions={[]}
      />
    ),
    information: information ? (
      <CollapsibleField header={labels.information}>
        <ReadOnlyText variant='body2'>{information}</ReadOnlyText>
      </CollapsibleField>
    ) : null,
    source: (
      <Stack spacing={0.5}>
        {isSourceExpected && (
          <ExtendedTypography variant='textSm'>
            {labels.source}
          </ExtendedTypography>
        )}
        {sourceInfo && (
          <Link href={sourceInfo.link} target='_blank'>
            <Typography fontWeight='bold'>
              {isSourceError
                ? ''
                : `${sourceInfo.name} - ${sourceInfo.entityType} - ${sourceInfo.id}`}
            </Typography>
          </Link>
        )}
      </Stack>
    ),
    dueDate: (
      <ControlledDateField
        name='dueDate'
        label={labels.dueDate}
        control={form.control}
        disablePast
        data-testid='dueDate'
      />
    ),
    ownerId: (
      <Controller
        name='ownerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            onChange={onChange}
            TextFieldProps={{
              label: labels.ownerId,
              'data-testid': 'ownerId',
              error: !!error?.message,
              helperText: error?.message,
            }}
          />
        )}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        label={labels.severity}
        control={form.control}
        options={severityValues}
        data-testid='severity'
      />
    ),
  };

  const formStateErrors = getFormStateErrors<CreateIssueRequestDto>(
    mapFormLabelsToFieldLabel(labels),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );

  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    issueTypeSubtypeQueryIsLoading,
    createIssueIsLoading: createIssue.isPending,
    createIssueIsSuccess: createIssue.isSuccess,
    fields,
    errors,
    hasErrors,
    onSubmit,
    cleanErrors,
    cleanForm: form.reset,
  };
};
